<template>
    <div class="search-container w-100">
        <div class="search-input product">
            <a
                v-show="text || alwaysShowClose"
                href="#"
                class="close-search"
                :class="{'no-icon': !showIcon}"
                @click.prevent="onCloseClick"
            >&times;</a>
            <a v-if="showIcon" href="#" class="search-icon" @click.prevent="onSearchIconClick">
                <img class="has-dark" src="@img/icons/search.svg" height="20" width="20" alt="search" />
            </a>
            <input
                v-model="text"
                class="form-control form-search"
                @keydown.down="hoverChange(1)"
                @keydown.up="hoverChange(-1)"
                @keyup.enter.prevent="openProduct(hoveredIndex)"
                @keyup.esc.prevent="escapePress()"
                @focusin="isFocused = true"
                @focusout="focusOut()"
                :placeholder="$t('Product Search')"
            >
        </div>

        <div
            class="search-results shadow"
            v-show="products.length > 0 && text.length > 0 && isFocused"
            @mouseover="mouseOnResults = true"
            @mouseout="mouseOnResults = false"
        >
            <div
                v-for="(product, index) in products"
                :key="product.id"
                class="row py-2 m-0 border-bottom search-result"
                @mouseover="hoveredIndex = index"
                @mouseout="hoveredIndex = null"
                @click="openProduct(index)"
                :class="{ 'selected': hoveredIndex === index }"
            >
                <div class="col-auto position-relative">
          <span
              class="product-search-badge"
              :class="getBadgeColorClass(product)"
              :title="getSentenceCase(product.availability.code)"
          >&nbsp;</span>
                    <img
                        v-if="product.images.small"
                        class="size-40"
                        :src="product.images.small"
                        :alt="product.name"
                    >
                    <span
                        v-else
                        class="product-no-image-circle size-40 d-block"
                    ></span>
                </div>
                <div class="col ps-0">
                    <div class="mb-2">{{ product.name }}</div>
                    <small
                        v-if="product.state === productStateActive && product.availability.code !== 'out_of_stock' && product.gross_price > 0"
                        class="fw-bold"
                        v-html="$formatter.price(product.gross_price)"
                    ></small>
                    <small
                        v-else
                        class="text-danger fw-bold"
                    >Out of stock</small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useNavigationStore } from "~/stores/NavigationStore";

export default {
    props: {
        callback: Function,
        searchOnly: Boolean,
        queryName: {
            type: String,
            default: 'search'
        },
        alwaysShowClose: {
            type: Boolean,
            default: false
        },
        emptyOnClose: Boolean,
        showIcon: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        productStateActive() {
            return 0;
        }
    },
    data() {
        return {
            text: useRoute().query[this.queryName] || '',
            products: [],
            isFocused: false,
            hoveredIndex: null,
            mouseOnResults: false,
            responseIndex: 0
        };
    },
    mounted() {
        this.$watch(
            () => this.$route.query?.term,
            (e) => {
                this.text = e || '';
            }
        );
    },
    emits: ['search', 'close'],
    watch: {
        text(newValue, oldValue) {
            if (newValue !== oldValue) {
                if (this.searchOnly) {
                    this.$emit('search', newValue);
                    this.emitClose();
                } else {
                    this.search();
                }
            }
        }
    },
    methods: {
        useNavigationStore,
        resetSearch() {
            const route = useRoute();
            const router = useRouter();

            const newQuery = { ...route.query };
            delete newQuery['term'];

            router.push({ path: route.path, query: newQuery });
            this.text = '';
            this.advancedSearch('');
        },
        async search() {
            if (!this.text) {
                return;
            }
            this.responseIndex++;
            let products = await getFromClient('products/search', { params: { term: this.text } });
            this.parseResults(products, this.responseIndex);
        },
        parseResults(data, responseIndex) {
            if (this.responseIndex === responseIndex) {
                this.products = data;
            }
            if (this.products.length - 1 < this.hoveredIndex) {
                this.hoveredIndex = null;
            }
        },
        focusOut() {
            if (!this.mouseOnResults) {
                this.isFocused = false;
            }
        },
        openProduct(productIndex) {
            this.isFocused = false;
            if (this.callback) {
                if (typeof productIndex === 'number') {
                    this.callback(this.products[productIndex].id);
                    this.emitClose();
                }
                return;
            }

            if (productIndex === null) {
                this.advancedSearch(this.text);
                this.emitClose();
                return;
            }
            this.$router.push(this.products[productIndex].url);
            this.emitClose();
        },
        onSearchIconClick() {
            this.advancedSearch(this.text);
        },
        async advancedSearch(term) {
            const store = useCatalogStore();
            if (store.paginator) {
                store.paginator.current_page = 1;
            }
            store.catalog.filters_selected = [];
            await store.fetchCatalog(term);
            this.$router.push(store.catalog.url);
        },
        hoverChange(direction) {
            if (this.hoveredIndex === null && this.products.length > 0) {
                if (direction === 1) {
                    this.hoveredIndex = 0;
                } else {
                    this.hoveredIndex = this.products.length - 1;
                }
                return;
            }
            if (typeof this.products[this.hoveredIndex + direction] !== 'undefined') {
                this.hoveredIndex += direction;
            } else {
                this.hoveredIndex = null;
            }
        },
        escapePress() {
            if (this.hoveredIndex === null) {
                this.text = '';
            } else {
                this.hoveredIndex = null;
            }
        },
        getSentenceCase(text) {
            if (text) {
                return text.replace(/(^\w)/, x => x.toUpperCase()).replace(/_/g, ' ');
            } else {
                return text;
            }
        },
        getBadgeColorClass(product) {
            if (product.state !== this.productStateActive || product.availability.code === 'out_of_stock') {
                return 'bg-red';
            }
            if (product.available_quantity > 0) {
                if (product.limited_mintage) {
                    return 'bg-orange';
                } else {
                    return 'bg-green';
                }
            }
            return 'bg-green';
        },
        emitClose() {
            if (this.emptyOnClose) {
                this.text = '';
            }
            this.$emit('close');
        },
        onCloseClick() {
            if (this.text) {
                this.resetSearch();
            } else if (this.alwaysShowClose) {
                this.emitClose();
            }
        }
    }
};
</script>
